import type { FormEvent } from 'react';
import { Input, FormField } from '@afrilink/ui';

/**
 * Registrant contact details — matches the shape both
 * `domain-register-confirm` and `domain-transfer-confirm` Edge Functions
 * accept on their `registrant` payload (and the underlying domains.co.za
 * `/domain` POST + `/domain/transfer` POST endpoints).
 */
export interface RegistrantInput {
  name: string;
  email: string;
  contactNumber: string;
  organisation?: string;
  address1: string;
  city: string;
  province: string;
  postalCode: string;
  /** ISO 3166-1 alpha-2, e.g. "ZA". Always uppercase. */
  country: string;
}

export const EMPTY_REGISTRANT: RegistrantInput = {
  name: '',
  email: '',
  contactNumber: '',
  organisation: '',
  address1: '',
  city: '',
  province: '',
  postalCode: '',
  country: 'ZA',
};

/**
 * Trim every string field on a `RegistrantInput`, returning a new object
 * suitable for sending to an Edge Function. Empty `organisation` becomes
 * undefined to match the EF shape (where organisation is optional).
 */
export function trimRegistrant(r: RegistrantInput): RegistrantInput {
  const org = (r.organisation ?? '').trim();
  return {
    name: r.name.trim(),
    email: r.email.trim(),
    contactNumber: r.contactNumber.trim(),
    organisation: org === '' ? undefined : org,
    address1: r.address1.trim(),
    city: r.city.trim(),
    province: r.province.trim(),
    postalCode: r.postalCode.trim(),
    country: r.country.trim().toUpperCase(),
  };
}

/**
 * Validate a `RegistrantInput`. Returns the first human-readable error
 * found, or null if everything checks out. Pure function so callers can
 * reuse it for form-level guards before calling an EF.
 *
 * Mirrors the server-side validation in `domain-transfer-confirm` and
 * `domain-register-confirm` so the customer doesn't see a server-rejected
 * payload that could have been caught client-side.
 */
export function validateRegistrant(
  r: RegistrantInput,
  opts: { requireAddress?: boolean } = {},
): string | null {
  // Contact identity is always required. Address is OPTIONAL for free bundled
  // .co.za (defaulted server-side to SightHost's address) but REQUIRED for paid
  // gTLDs, where the registry mandates a full postal address up-front — pass
  // { requireAddress: true } for those.
  const required: Array<[keyof RegistrantInput, string]> = [
    ['name', 'Full name'],
    ['email', 'Email'],
    ['contactNumber', 'Phone'],
    ['country', 'Country code'],
  ];
  if (opts.requireAddress) {
    required.push(
      ['address1', 'Address line 1'],
      ['city', 'City'],
      ['province', 'Province'],
      ['postalCode', 'Postal code'],
    );
  }
  for (const [k, label] of required) {
    const v = (r[k] ?? '').toString().trim();
    if (!v) return `${label} is required.`;
  }
  if (!/^[A-Z]{2}$/.test(r.country.trim())) {
    return 'Country must be a 2-letter ISO code (e.g. ZA).';
  }
  return null;
}

interface Props {
  value: RegistrantInput;
  onChange: (next: RegistrantInput) => void;
  disabled?: boolean;
  /** ID prefix to keep label/input pairs unique on pages that render two forms. */
  idPrefix?: string;
  /**
   * When true (paid gTLDs), the postal-address fields become mandatory and lose
   * their "(optional)" labels. Free bundled .co.za leaves this false — its blank
   * address is defaulted to SightHost's company address server-side.
   */
  requireAddress?: boolean;
}

/**
 * Shared registrant form, used by both:
 *   - `RegisterDomainConfirmPage` (domain registration)
 *   - `TransferDomainPage` Step 3 (transfer-in)
 *
 * Renders the eight fields ZACR / ICANN require on registration and
 * transfer. Pure controlled component — no internal state, no submit.
 * The parent owns submission, loading state, and EF wiring.
 */
export function RegistrantForm({ value, onChange, disabled, idPrefix = 'r', requireAddress = false }: Props) {
  function set<K extends keyof RegistrantInput>(key: K, v: RegistrantInput[K]) {
    onChange({ ...value, [key]: v });
  }
  const addrLabel = (base: string) => (requireAddress ? base : `${base} (optional)`);

  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      <FormField label="Full name" htmlFor={`${idPrefix}-name`}>
        <Input
          id={`${idPrefix}-name`}
          value={value.name}
          onChange={(e: FormEvent<HTMLInputElement>) =>
            set('name', (e.target as HTMLInputElement).value)
          }
          disabled={disabled}
          required
        />
      </FormField>

      <FormField label="Email" htmlFor={`${idPrefix}-email`}>
        <Input
          id={`${idPrefix}-email`}
          type="email"
          value={value.email}
          onChange={(e: FormEvent<HTMLInputElement>) =>
            set('email', (e.target as HTMLInputElement).value)
          }
          disabled={disabled}
          required
        />
      </FormField>

      <FormField label="Phone" htmlFor={`${idPrefix}-phone`} helper="Format: +27.123456789">
        <Input
          id={`${idPrefix}-phone`}
          value={value.contactNumber}
          onChange={(e: FormEvent<HTMLInputElement>) =>
            set('contactNumber', (e.target as HTMLInputElement).value)
          }
          disabled={disabled}
          required
        />
      </FormField>

      <FormField label="Organisation (optional)" htmlFor={`${idPrefix}-org`}>
        <Input
          id={`${idPrefix}-org`}
          value={value.organisation ?? ''}
          onChange={(e: FormEvent<HTMLInputElement>) =>
            set('organisation', (e.target as HTMLInputElement).value)
          }
          disabled={disabled}
        />
      </FormField>

      <FormField label={addrLabel('Address line 1')} htmlFor={`${idPrefix}-addr`}>
        <Input
          id={`${idPrefix}-addr`}
          value={value.address1}
          onChange={(e: FormEvent<HTMLInputElement>) =>
            set('address1', (e.target as HTMLInputElement).value)
          }
          disabled={disabled}
          required={requireAddress}
        />
      </FormField>

      <FormField label={addrLabel('City')} htmlFor={`${idPrefix}-city`}>
        <Input
          id={`${idPrefix}-city`}
          value={value.city}
          onChange={(e: FormEvent<HTMLInputElement>) =>
            set('city', (e.target as HTMLInputElement).value)
          }
          disabled={disabled}
          required={requireAddress}
        />
      </FormField>

      <FormField label={addrLabel('Province')} htmlFor={`${idPrefix}-prov`}>
        <Input
          id={`${idPrefix}-prov`}
          value={value.province}
          onChange={(e: FormEvent<HTMLInputElement>) =>
            set('province', (e.target as HTMLInputElement).value)
          }
          disabled={disabled}
          required={requireAddress}
        />
      </FormField>

      <FormField label={addrLabel('Postal code')} htmlFor={`${idPrefix}-pc`}>
        <Input
          id={`${idPrefix}-pc`}
          value={value.postalCode}
          onChange={(e: FormEvent<HTMLInputElement>) =>
            set('postalCode', (e.target as HTMLInputElement).value)
          }
          disabled={disabled}
          required={requireAddress}
        />
      </FormField>

      <FormField label="Country (ISO 3166-1 alpha-2)" htmlFor={`${idPrefix}-country`}>
        <Input
          id={`${idPrefix}-country`}
          maxLength={2}
          value={value.country}
          onChange={(e: FormEvent<HTMLInputElement>) =>
            set('country', (e.target as HTMLInputElement).value.toUpperCase())
          }
          disabled={disabled}
          required
          className="uppercase"
        />
      </FormField>
    </div>
  );
}
